// SPDX-License-Identifier: MIT
/**
 * Device frames and the pointer. Frames are generic on purpose: no vendor's window buttons, notch,
 * status bar or clock, so a film never borrows another company's trade dress.
 */
import { TAU, clamp } from '../../core/math.ts';
import { mix } from '../color.ts';
import { curves } from '../curves.ts';
import { type Box, elevation, fitLabel, panel, rrect } from './draw.ts';
import type { UiTokens } from './tokens.ts';
type Ctx = CanvasRenderingContext2D;
const BAR = 64;
/**
 * A browser window: neutral dots, the brand's own address centred in the bar. Returns the content
 * box below the bar (clip to it with `rrect` + `clip` and the frame's radius for rounded corners).
 */
export function browser(ctx: Ctx, tk: UiTokens, b: Box, { url = '', lift = 3 }: {
    url?: string;
    lift?: number;
} = {}): Box {
    const r = tk.radius.lg;
    panel(ctx, tk, b, { r, fill: tk.surface, lift });
    ctx.fillStyle = tk.border;
    ctx.fillRect(b.x, b.y + BAR, b.w, 1.5);
    for (let i = 0; i < 3; i++) {
        ctx.beginPath();
        ctx.arc(b.x + 32 + i * 24, b.y + BAR / 2, 7, 0, TAU);
        ctx.fillStyle = tk.track;
        ctx.fill();
    }
    if (url) {
        const w = Math.min(b.w * 0.46, 600);
        const pill: Box = { x: b.x + (b.w - w) / 2, y: b.y + 12, w, h: BAR - 24 };
        rrect(ctx, pill, pill.h / 2);
        ctx.fillStyle = tk.sunken;
        ctx.fill();
        fitLabel(ctx, tk, url, w - 40, tk.type.caption, 500);
        ctx.fillStyle = tk.muted;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(url, pill.x + w / 2, pill.y + pill.h / 2 + 1);
    }
    return { x: b.x, y: b.y + BAR + 1.5, w: b.w, h: b.h - BAR - 1.5 };
}
/** A generic phone: rounded body, even bezel, no notch. Returns the screen box. */
export function phone(ctx: Ctx, tk: UiTokens, b: Box, { lift = 3 }: {
    lift?: number;
} = {}): Box {
    const r = Math.min(b.w * 0.15, 76);
    elevation(ctx, tk, b, r, lift);
    rrect(ctx, b, r);
    ctx.fillStyle = tk.dark ? mix(tk.bg, '#000000', 0.5) : mix(tk.text, '#000000', 0.35);
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = tk.dark ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.08)';
    ctx.stroke();
    const bezel = Math.max(10, b.w * 0.035);
    const screen: Box = { x: b.x + bezel, y: b.y + bezel, w: b.w - 2 * bezel, h: b.h - 2 * bezel };
    rrect(ctx, screen, r - bezel);
    ctx.fillStyle = tk.surface;
    ctx.fill();
    return screen;
}
/** Arrow outline, tip at the origin, about 40 px tall. */
const ARROW: Array<[
    number,
    number
]> = [
    [0, 0],
    [0, 33],
    [8.5, 25.5],
    [14, 38],
    [20, 35.5],
    [14.5, 23],
    [25.5, 23],
];
const ARROW_W = Math.max(...ARROW.map((p) => p[0]));
const ARROW_H = Math.max(...ARROW.map((p) => p[1]));
function arrowPath(ctx: Ctx): void {
    ctx.beginPath();
    ARROW.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    ctx.closePath();
}
/**
 * The pointer with its tip at (x, y). Pressing shrinks it a little towards the tip. It reports
 * itself to QA as the `pointer` element, which warns when it rests on words.
 */
export function cursor(ctx: Ctx, tk: UiTokens, x: number, y: number, { press = 0, scale = 1 } = {}): void {
    const fill = tk.dark ? '#FFFFFF' : '#111111';
    const edge = tk.dark ? '#111111' : '#FFFFFF';
    const s = scale * (1 - 0.12 * press);
    tk.report?.element('pointer', [x, y, ARROW_W * s, ARROW_H * s]);
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(s, s);
    ctx.lineJoin = 'round';
    // A soft offset copy stands in for a blurred shadow.
    ctx.save();
    ctx.translate(2, 4);
    arrowPath(ctx);
    ctx.fillStyle = 'rgba(0,0,0,0.22)';
    ctx.fill();
    ctx.restore();
    arrowPath(ctx);
    ctx.lineWidth = 3;
    ctx.strokeStyle = edge;
    ctx.stroke();
    ctx.fillStyle = fill;
    ctx.fill();
    ctx.restore();
}
/** The ring a click leaves behind; `age` in beats since the click (fades out by 0.5). */
export function clickRipple(ctx: Ctx, tk: UiTokens, x: number, y: number, age: number): void {
    if (!(age >= 0 && age < 0.5))
        return;
    const k = curves.glide(age / 0.5);
    const r = 10 + 46 * k;
    const a = clamp(1 - age / 0.5);
    ctx.beginPath();
    ctx.arc(x, y, r, 0, TAU);
    ctx.fillStyle = mix(tk.accent, tk.bg, 0.2);
    const prev = ctx.globalAlpha;
    ctx.globalAlpha = prev * a * 0.22;
    ctx.fill();
    ctx.globalAlpha = prev * a * 0.7;
    ctx.lineWidth = 3;
    ctx.strokeStyle = tk.accent;
    ctx.stroke();
    ctx.globalAlpha = prev;
}
